import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { acquisitionApi } from "../../api/acquisition";
import {
  acquisitionDeviceMetadata,
  acquisitionEventId,
  acquisitionVisitorId,
  saveAcquisitionDraft,
} from "../../utils/acquisitionFunnel";
import {
  PUBLIC_REGISTRATION_VERSION,
  publicRegistrationContext,
  publicRegistrationPath,
  registrationFieldErrors,
} from "../../utils/publicRegistration";
import { EntryIcon } from "../../components/home/PublicEntryUI";
import PublicAuthLayout from "../../components/home/PublicAuthLayout";

// # SIGNUP: three fields, one submit, then the existing authenticated dashboard.
// No project is provisioned and no module is purchased in this public flow.
export default function RegisterPage() {
  const { register, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { params, sessionId, referralCode, campaign } = React.useMemo(
    () => publicRegistrationContext(search),
    [search],
  );
  const entryPoint = String(params.get("entry") || "register-direct").slice(
    0,
    80,
  );
  const visitorId = React.useMemo(() => acquisitionVisitorId(), []);
  const loginHref = publicRegistrationPath(search, "register-login", "/login");
  const homeHref = publicRegistrationPath(search, "", "/");
  const [form, setForm] = React.useState({ name: "", email: "", password: "" });
  const [errors, setErrors] = React.useState({});
  const [serverError, setServerError] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [capsLock, setCapsLock] = React.useState(false);
  const submittingRef = React.useRef(false);
  const submittedRef = React.useRef(false);
  const formRef = React.useRef(null);
  const errorRef = React.useRef(null);

  const track = React.useCallback(
    (action, metadata = {}) => {
      acquisitionApi
        .track({
          action,
          sessionId,
          visitorId,
          eventId: acquisitionEventId("signup"),
          metadata: {
            stepKey: "account",
            stepIndex: 1,
            totalSteps: 2,
            progressPct: 50,
            entryPoint,
            audience: authLoading
              ? "unknown"
              : user
                ? "authenticated"
                : "anonymous",
            experienceVersion: PUBLIC_REGISTRATION_VERSION,
            ...acquisitionDeviceMetadata(),
            ...metadata,
          },
        })
        .catch(() => null);
    },
    [authLoading, entryPoint, sessionId, user, visitorId],
  );

  React.useEffect(() => {
    const payload = {
      sessionId,
      visitorId,
      referralCode,
      campaign,
      entryPoint,
      experienceVersion: PUBLIC_REGISTRATION_VERSION,
    };
    saveAcquisitionDraft(payload);
    acquisitionApi
      .startSession({ ...payload, audience: "unknown" })
      .catch(() => null);
    acquisitionApi
      .track({
        action: "acquisition_step_viewed",
        sessionId,
        visitorId,
        eventId: `account_${sessionId}_${PUBLIC_REGISTRATION_VERSION}`.slice(
          0,
          60,
        ),
        metadata: {
          stepKey: "account",
          stepIndex: 1,
          totalSteps: 2,
          progressPct: 50,
          entryPoint,
          experienceVersion: PUBLIC_REGISTRATION_VERSION,
          ...acquisitionDeviceMetadata(),
        },
      })
      .catch(() => null);
  }, [campaign, entryPoint, referralCode, sessionId, visitorId]);

  React.useEffect(() => {
    if (authLoading) return;
    acquisitionApi
      .startSession({
        sessionId,
        visitorId,
        referralCode,
        campaign,
        entryPoint,
        audience: user ? "authenticated" : "anonymous",
        experienceVersion: PUBLIC_REGISTRATION_VERSION,
      })
      .catch(() => null);
    if (user && !submittedRef.current)
      navigate("/dashboard", { replace: true });
  }, [
    authLoading,
    campaign,
    entryPoint,
    navigate,
    referralCode,
    sessionId,
    user,
    visitorId,
  ]);

  React.useEffect(() => {
    if (serverError) errorRef.current?.focus();
  }, [serverError]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((previous) => ({ ...previous, [name]: value }));
    setErrors((previous) => ({ ...previous, [name]: "" }));
    setServerError("");
  };
  const handleBlur = (event) => {
    const key = event.target.name;
    const next = registrationFieldErrors(form);
    setErrors((previous) => ({ ...previous, [key]: next[key] || "" }));
  };
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    // Do not let an older /auth/me response overwrite a newly created session.
    // The form stays visible and editable while that initial check resolves.
    if (authLoading) return;
    if (user) {
      navigate("/dashboard", { replace: true });
      return;
    }
    const nextErrors = registrationFieldErrors(form);
    setErrors(nextErrors);
    const firstInvalid = ["name", "email", "password"].find(
      (key) => nextErrors[key],
    );
    if (firstInvalid) {
      formRef.current?.elements.namedItem(firstInvalid)?.focus();
      track("acquisition_step_validation_error", { reason: firstInvalid });
      return;
    }
    submittingRef.current = true;
    submittedRef.current = true;
    setSubmitting(true);
    setServerError("");
    // # RESILIENCE: never await analytics before the real account request.
    track("acquisition_registration_started");
    try {
      const data = await register({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        referralCode: referralCode || undefined,
        prospectSessionId: sessionId,
      });
      track("acquisition_step_completed", { progressPct: 100 });
      navigate("/dashboard", {
        replace: true,
        state: {
          onboardingMessage: data?.onboarding?.requiresEmailVerification
            ? "Cuenta creada. Podés verificar tu email cuando quieras."
            : data?.onboarding?.message,
        },
      });
    } catch (error) {
      setServerError(
        error.message || "No pudimos crear tu cuenta. Intentá de nuevo.",
      );
      submittedRef.current = false;
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <PublicAuthLayout
      homeHref={homeHref}
      accountHref={loginHref}
      accountPrompt="¿Ya tenés cuenta?"
      accountLabel="Ingresar"
      eyebrow="EMPEZÁ POR ACÁ"
      title="Creá tu cuenta gratis."
      titleId="wp-signup-title"
      subtitle="Tus datos para entrar. Tu negocio lo configurás después."
      storyEyebrow="UN PRIMER PASO. MÁS POSIBILIDADES."
      storyTitle={
        <>
          Empezá simple.
          <br />
          <span>Crecé a tu ritmo.</span>
        </>
      }
      storyDescription="Empezá con una página web gratis y construí desde ahí. Sin tener que resolverlo todo el primer día."
      benefits={[
        "Entrás directamente a tu panel.",
        "Configurás tu negocio a tu ritmo.",
        "Elegís qué herramientas sumar después.",
      ]}
      storyNote="La página web es gratuita. Los módulos adicionales se activan por separado según tu plan."
    >
      {referralCode && (
        <p className="wp-signup-referral">
          Código de referido: <strong>{referralCode}</strong>
        </p>
      )}
      {serverError && (
        <div
          className="wp-signup-alert"
          role="alert"
          tabIndex={-1}
          ref={errorRef}
        >
          {serverError}
          {/registrad|exist/i.test(serverError) && (
            <>
              <br />
              <Link to={loginHref}>Ingresar con mi cuenta</Link>
            </>
          )}
        </div>
      )}
      <form
        className="wp-signup-form"
        noValidate
        onSubmit={handleSubmit}
        ref={formRef}
        aria-busy={submitting}
      >
        <div className="wp-signup-field">
          <label htmlFor="wp-signup-name">Tu nombre</label>
          <input
            id="wp-signup-name"
            name="name"
            type="text"
            autoComplete="name"
            autoCapitalize="words"
            placeholder="Cómo te llamás"
            value={form.name}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={submitting}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "wp-signup-name-error" : undefined}
          />
          {errors.name && (
            <p id="wp-signup-name-error" className="wp-signup-field-error">
              {errors.name}
            </p>
          )}
        </div>
        <div className="wp-signup-field">
          <label htmlFor="wp-signup-email">Tu email</label>
          <input
            id="wp-signup-email"
            name="email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            placeholder="nombre@correo.com"
            value={form.email}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            disabled={submitting}
            aria-invalid={Boolean(errors.email)}
            aria-describedby={
              errors.email ? "wp-signup-email-error" : undefined
            }
          />
          {errors.email && (
            <p id="wp-signup-email-error" className="wp-signup-field-error">
              {errors.email}
            </p>
          )}
        </div>
        <div className="wp-signup-field">
          <label htmlFor="wp-signup-password">Elegí una contraseña</label>
          <div className="wp-signup-password-wrap">
            <input
              id="wp-signup-password"
              name="password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              autoCapitalize="none"
              spellCheck={false}
              minLength={8}
              maxLength={128}
              value={form.password}
              onChange={handleChange}
              onBlur={(event) => {
                handleBlur(event);
                setCapsLock(false);
              }}
              onKeyUp={(event) =>
                setCapsLock(Boolean(event.getModifierState?.("CapsLock")))
              }
              required
              disabled={submitting}
              aria-invalid={Boolean(errors.password)}
              aria-describedby={`${errors.password ? "wp-signup-password-error" : "wp-signup-password-help"}${capsLock ? " wp-signup-caps" : ""}`}
            />
            <button
              type="button"
              aria-label={
                showPassword ? "Ocultar contraseña" : "Mostrar contraseña"
              }
              aria-pressed={showPassword}
              onClick={() => setShowPassword((value) => !value)}
              disabled={submitting}
            >
              <EntryIcon name={showPassword ? "eyeOff" : "eye"} size={20} />
            </button>
          </div>
          {errors.password ? (
            <p id="wp-signup-password-error" className="wp-signup-field-error">
              {errors.password}
            </p>
          ) : (
            <p id="wp-signup-password-help" className="wp-signup-field-note">
              Al menos 8 caracteres. Podés pegar una contraseña segura.
            </p>
          )}
          {capsLock && (
            <p
              id="wp-signup-caps"
              className="wp-signup-field-note"
              role="status"
            >
              Tenés las mayúsculas activadas.
            </p>
          )}
        </div>
        <button
          type="submit"
          className="wp-entry-primary wp-signup-submit"
          disabled={submitting || authLoading}
        >
          {submitting ? (
            <>
              <span className="wp-entry-spinner" aria-hidden="true" />
              Creando tu cuenta…
            </>
          ) : authLoading ? (
            "Comprobando sesión…"
          ) : (
            <>
              Crear cuenta gratis
              <EntryIcon size={19} />
            </>
          )}
        </button>
        <p className="wp-signup-reassurance">
          <EntryIcon name="lock" size={14} />
          Registro gratuito. No te pedimos tarjeta.
        </p>
      </form>
      <p className="wp-signup-mobile-login">
        ¿Ya tenés cuenta? <Link to={loginHref}>Ingresar</Link>
      </p>
      <details className="wp-signup-details">
        <summary>¿Qué pasa después?</summary>
        <p>
          Entrás a tu panel para crear tu proyecto y empezar a configurar tu
          negocio. Te enviaremos un email para verificar tu dirección; podés
          hacerlo después, sin bloquear el acceso.
        </p>
      </details>
    </PublicAuthLayout>
  );
}
